import { useMemo } from "react";

import {
    AllValueType,
    combineCollections,
    DISABLED_APY_BIGINT,
    filterNullableRecord,
    filterTruthy,
    getUnixTs,
    isAll,
    lamportsToUiAmount,
    mapDefined,
    NullableRecord,
    safeBigIntToNumber,
    SortDirection,
    TIME,
    unique
} from "@bridgesplit/utils";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import Decimal from "decimal.js";
import { useMemoizedGroupedMap } from "@bridgesplit/ui";
import { StakeLpQueryParams, UnstakeLpQueryParams } from "@bridgesplit/abf-sdk";

import {
    LendingVaultAllocationExpanded,
    LendingVaultExpanded,
    LendVaultSortType,
    LoopscalePointsAction,
    ParsedVaultRewardsScheduleExpanded,
    UserVaultPositionStatus,
    UserLendingVaultFilter,
    RewardMint,
    UserVaultExternalRewardsPnlResponse,
    UserVaultPnl,
    UserVaultPositionExpanded,
    VaultReward,
    AggregatedUserExternalRewardsPnlResponse,
    UserExternalRewardsInfoExpanded,
    VaultApyBreakdown,
    FetchLendVaultsInfoParams,
    StakeSortType,
    UserPositionDepositSummaryParams,
    ParsedLendVaultAction,
    VaultActionType,
    ParsedAllowedStakeDuration,
    UserVaultPositionAmounts,
    StrategyExpandedInfo,
    StrategyInfo,
    LendingVaultMetadataExpanded,
    TimeLockStatus,
    VaultMetadataTag,
    CapType,
    MaxVaultWithdraw,
    TokenListTag,
    TokenListMetadata,
    UserVaultGroup,
    UserVaultsFilter
} from "../types/";
import {
    useLendingVaultAllocationsQuery,
    useLendingVaultInfosQuery,
    useLendingVaultPositionsQuery,
    useUserPositionDepositSummaryQuery,
    useUserVaultExternalRewardsPnlQuery,
    useUserVaultsGroupsQuery,
    useVaultAccessQuery
} from "../reducers";
import type { ApiUserVaultGroup } from "../types/api/vault";
import { VaultDepositResponse, VaultDepositsAggregate } from "../types/domain";
import { useOraclePrices } from "./pricing";
import { useAbfTypesToUiConverter } from "../utils/ui";
import { useMarketOracleInfos, useStrategyDurations } from "./markets";
import { useLiveClock, PORTFOLIO_TICK } from "./clock";
import { useExpandStrategyResponse, buildStrategyInfoResponse } from "./strategies";
import { useGenerateUiExpanded, getVaultMaxAmountFromCap, isLendVaultComingSoon } from "../utils";
import {
    MAX_SANE_VAULT_LOAN_COLLATERAL_USD,
    MAXIMUM_PAGE_SIZE,
    USDC_GENESIS_PRINCIPAL_MINT,
    USDC_GENESIS_TRUE_LP_PRICE,
    USDC_GENESIS_VAULT_ADDRESS
} from "../constants";
import { useLatestCollateralYieldHistoryByMint } from "./loop";
import { useLoopscaleWalletContext } from "../wallet";
import { useTokenListContext } from "./token-list";
import { useTimelocks } from "./timelocks";

type Params = {
    skip?: boolean;
    filter: Partial<FetchLendVaultsInfoParams>;
    // Skip the pending-timelocks fetch. Only vault detail / manager-view consume `pendingTerms`;
    // listing callers (lend page, farming, portfolio Vaults section) can opt out.
    skipTimelocks?: boolean;
};

export interface LendVaultAccessGate {
    isAllowed: boolean;
    isComingSoon: boolean;
    isAccessLoading: boolean;
}

// Coming-soon vaults are only open to wallets allowlisted via a `vault_allowed` wallet-policy entry.
// An allowlisted wallet sees the vault as a normal live vault instead of the coming-soon placeholder.
export function useLendVaultAccessGate(vaultExpanded: LendingVaultExpanded | undefined): LendVaultAccessGate {
    const {
        data: { userWalletAddress: activeWallet }
    } = useLoopscaleWalletContext();

    const comingSoon = isLendVaultComingSoon(vaultExpanded);
    const vaultAddress = vaultExpanded?.vault.address;
    const shouldCheckAccess = comingSoon && !!activeWallet && !!vaultAddress;

    const { data, isLoading } = useVaultAccessQuery(shouldCheckAccess && vaultAddress ? { vaultAddress } : skipToken);

    const isAllowlisted = !!activeWallet && (data?.allowed ?? false);

    return {
        // A disconnected wallet is not blocked here so the normal connect-wallet flow can proceed.
        isAllowed: !comingSoon || !activeWallet || isAllowlisted,
        isComingSoon: comingSoon && !isAllowlisted,
        // `isLoading` rather than `isFetching` so background refetches don't flicker the CTA.
        isAccessLoading: shouldCheckAccess && isLoading
    };
}

export function useLendVaults({ filter, skip: skipParam, skipTimelocks }: Params) {
    const rawFilter = useMemo((): FetchLendVaultsInfoParams => {
        // Canonicalize so semantically-identical "no filter" callers (e.g. {principalMints:[]} vs
        // {vaultAddresses:[]}) serialize byte-identically — the RTK cache key is JSON.stringify(args)
        // (abfSerializeQueryArgs). Spread `filter` so new params aren't silently dropped, then sort keys
        // (stable order) and drop undefined / empty-array values (omitted == "no filter").
        const merged: FetchLendVaultsInfoParams = {
            sortType: filter.sortType ?? LendVaultSortType.TotalDeposits,
            sortDirection: filter.sortDirection ?? SortDirection.Desc,
            page: filter.page ?? 0,
            pageSize: filter.pageSize ?? 20,
            ...filter,
            // /lending_vaults/info already returns per-collateral assetTerms; just add the USD/APY summary
            // so vaultStrategy can be built entirely from this response — no /strategy/{infos,summaries} call.
            includeStrategySummaries: true
        };
        return Object.keys(merged)
            .sort()
            .reduce((acc, key) => {
                const typedKey = key as keyof FetchLendVaultsInfoParams;
                const val = merged[typedKey];
                if (val !== undefined && (!Array.isArray(val) || val.length > 0)) {
                    acc[typedKey] = val as never;
                }
                return acc;
            }, {} as FetchLendVaultsInfoParams);
    }, [filter]);

    const query = useLendingVaultInfosQuery(rawFilter, {
        skip: skipParam
    });

    const vaultPrincipalMints = unique(query.data?.lendVaults?.map((v) => v.vault.principalMint) ?? []);

    const { strategyDurations } = useStrategyDurations();
    const { getMetadata, isLoading: metadataLoading } = useTokenListContext();

    const { data: marketData, isFetching: marketFetching } = useMarketOracleInfos({
        filter: {
            principalMints: vaultPrincipalMints ?? [],
            addresses: query.data?.lendVaults?.map((v) => v.vaultStrategy.strategy.marketInformation) ?? []
        }
    });

    // Reconstruct the strategy item shape locally from the inlined response — no second strategy call.
    // vaultStrategy carries the base strategy + caps + external yield + per-collateral assetTerms;
    // strategySummary carries the USD/APY numbers it omits.
    const inlinedStrategyResponse = useMemo(() => {
        if (!query.data) return undefined;
        // Drop vaults without a live strategy summary (e.g. coming-soon vaults) — they render with a
        // null vaultStrategy downstream, same as before. Guard vaultStrategy too so a missing one can't
        // produce a strategy-less item that crashes the expansion at d.strategy.marketInformation.
        const items = mapDefined(query.data.lendVaults, (v): StrategyInfo | undefined =>
            v.vaultStrategy && v.strategySummary ? { ...v.vaultStrategy, ...v.strategySummary } : undefined
        );
        return buildStrategyInfoResponse(items);
    }, [query.data]);

    const {
        data: strategies,
        isLoading: strategiesLoading,
        isFetching: strategiesFetching
    } = useExpandStrategyResponse(inlinedStrategyResponse, query.isLoading, query.isFetching, skipParam);

    const { timelocks: timelocksData, isLoading: timelocksLoading } = useTimelocks({
        filter: { vaultAddresses: rawFilter.vaultAddresses, timelockStatus: TimeLockStatus.Pending },
        skip: skipTimelocks
    });

    const { convertLendingVaultInfo, isLoading: isTypeToUiLoading } = useAbfTypesToUiConverter();
    const { createLendingVaultLtvInfo, isLoading: uiLoading } = useGenerateUiExpanded();

    const strategyToVault = useMemo(() => {
        if (!strategies) return undefined;
        return new Map(strategies.map((s) => [s.strategy.address, s]));
    }, [strategies]);
    const vaultAddressToTimelock = useMemoizedGroupedMap(timelocksData ?? [], (timelock) => timelock.vaultAddress);

    // Strategy loading is intentionally excluded from the base isLoading/isFetching: vault data
    // (metadata, deposits, history) resolves independently so components that don't read `vaultStrategy`
    // are not blocked by a slow strategy fetch. `strategiesLoading`/`strategiesFetching` are returned
    // separately for list callers that want to hold their skeleton until strategy-derived stats arrive.
    const isFetching = query.isFetching || marketFetching;
    const isLoading = metadataLoading || query.isLoading || timelocksLoading || isTypeToUiLoading || uiLoading;

    const data = useMemo(() => {
        if (!query.data || isLoading) return undefined;

        return query.data.lendVaults
            .map((vaultInfoRaw): NullableRecord<LendingVaultExpanded> => {
                const vaultInfo = convertLendingVaultInfo(vaultInfoRaw);
                const strategyExpanded = strategyToVault?.get(vaultInfo.vaultStrategy.strategy.address);

                const lendingVaultMetadataExpanded: LendingVaultMetadataExpanded = {
                    ...vaultInfo.vaultMetadata,
                    isGenesisVault: vaultInfo.vaultMetadata.tags?.includes(VaultMetadataTag.Genesis) ?? false
                };

                const vaultMarketInfo = marketData?.find(
                    (info) => info.marketInformation.address === vaultInfo.vaultStrategy.strategy.marketInformation
                );

                const rewardsExpanded: ParsedVaultRewardsScheduleExpanded[] = mapDefined(
                    vaultInfo.vaultRewardsSchedules,
                    (reward) => {
                        const metadata = getMetadata(reward.rewardMint);
                        if (!metadata) return undefined;
                        return {
                            ...reward,
                            rewardMetadata: metadata
                        };
                    }
                );

                const ltvInfos = createLendingVaultLtvInfo(vaultInfoRaw, vaultMarketInfo, strategyDurations);

                // `?? null` (never undefined) for every vault: `strategyExpanded` is undefined both for
                // coming-soon vaults (no live strategy on-chain) and transiently while the strategy query
                // is in flight (loading is decoupled from base data). filterNullableRecord drops records
                // with any undefined field, so null keeps the vault visible with strategy-derived fields
                // pending; consumers already handle a null vaultStrategy.
                return {
                    ...vaultInfo,
                    totalOutstandingLoanCollateralValueUsd:
                        vaultInfo.totalOutstandingLoanCollateralValueUsd > MAX_SANE_VAULT_LOAN_COLLATERAL_USD
                            ? 0
                            : vaultInfo.totalOutstandingLoanCollateralValueUsd,
                    pendingTerms: vaultAddressToTimelock?.get(vaultInfo.vault.address) ?? [],
                    vaultMetadata: lendingVaultMetadataExpanded,
                    vaultStrategy: withGenesisApyCleared(strategyExpanded) ?? null,
                    vaultRewardsSchedules: rewardsExpanded,
                    principalMetadata: getMetadata(vaultInfo.vault.principalMint),
                    lpMetadata: getMetadata(vaultInfo.vault.lpMint) ?? null, // Dont want to filter when undefined
                    ltvInfos
                };
            })
            .filter(filterNullableRecord);
    }, [
        query.data,
        isLoading,
        convertLendingVaultInfo,
        strategyToVault,
        marketData,
        createLendingVaultLtvInfo,
        strategyDurations,
        vaultAddressToTimelock,
        getMetadata
    ]);

    return {
        data,
        totalDataCount: query.data?.total,
        isLoading,
        isFetching,
        // Strategy status, decoupled from base loading. List callers fold these into their own skeleton
        // gate so strategy-derived stats (APY/TVL/deposits) don't flash zeroes before the strategy loads.
        strategiesLoading,
        strategiesFetching
    };
}

export function useLendVaultByVaultIdentifier(vaultIdentifier: string | undefined, includeRewards?: boolean) {
    const {
        data: allVaults,
        isLoading,
        isFetching
    } = useLendVaults({
        filter: {
            vaultIdentifiers: vaultIdentifier ? [vaultIdentifier] : undefined,
            page: 0,
            pageSize: 1,
            sortType: LendVaultSortType.TotalDeposits,
            sortDirection: SortDirection.Asc,
            includeRewards
        },
        skip: !vaultIdentifier
        // Vault detail consumes pendingTerms, so timelocks are NOT skipped here.
    });

    const vaultDataByVaultIdentifier = allVaults?.find(
        (v) => v.vault.vaultIdentifier?.toLowerCase() === vaultIdentifier?.toLowerCase()
    );

    const skipAddressLookup = isLoading || !!vaultDataByVaultIdentifier;

    const {
        data: addressData,
        isLoading: addressLoading,
        isFetching: addressFetching
    } = useLendVaultByAddress(skipAddressLookup ? undefined : vaultIdentifier);

    return {
        data: vaultDataByVaultIdentifier || addressData,
        isLoading: isLoading || addressLoading,
        isFetching: isFetching || addressFetching
    };
}

export function useLendVaultByAddress(vaultAddress: string | undefined) {
    const {
        data: allVaults,
        isLoading,
        isFetching
    } = useLendVaults({
        filter: {
            vaultAddresses: vaultAddress ? [vaultAddress] : undefined,
            page: 0,
            pageSize: 1,
            sortType: LendVaultSortType.TotalDeposits,
            sortDirection: SortDirection.Asc
        },
        skip: !vaultAddress
    });

    return { data: allVaults?.find((v) => v.vault.address === vaultAddress), isLoading, isFetching };
}

// Temporary incident patch: the USDC Genesis strategy's APY comes from the same inflated interest
function withGenesisApyCleared<T extends { strategy: { lender: string; wAvgApy: number } }>(
    strategyExpanded: T | undefined
): T | undefined {
    if (strategyExpanded?.strategy.lender !== USDC_GENESIS_VAULT_ADDRESS) return strategyExpanded;
    return { ...strategyExpanded, strategy: { ...strategyExpanded.strategy, wAvgApy: 0 } };
}

export function isVaultTemporarilyUnavailable(vaultExpanded: LendingVaultExpanded | undefined) {
    return vaultExpanded?.vault.address === USDC_GENESIS_VAULT_ADDRESS;
}

export function getVaultLpVirtualPrice(vaultExpanded: LendingVaultExpanded | undefined, currentTimestamp: number) {
    if (!vaultExpanded) return undefined;
    const netAssetValue = getVaultNetAssetValue(vaultExpanded, currentTimestamp);
    const lpSupply = vaultExpanded.vault.lpSupply;
    return netAssetValue ? netAssetValue / lpSupply : undefined;
}

export function getVaultNetAssetValue(
    vaultExpanded: LendingVaultExpanded | undefined,
    currentTimestamp: number,
    options: { ignoreFees: boolean } = { ignoreFees: false }
) {
    if (!vaultExpanded || !vaultExpanded.vaultStrategy) return undefined;
    // Overriding NAV (not just the LP price) keeps share price, TVL and position values consistent.
    if (isVaultTemporarilyUnavailable(vaultExpanded)) {
        return USDC_GENESIS_TRUE_LP_PRICE === undefined
            ? undefined
            : USDC_GENESIS_TRUE_LP_PRICE * vaultExpanded.vault.lpSupply;
    }
    const { ignoreFees = false } = options;
    const principalDeployed = vaultExpanded.vaultStrategy.strategy.currentDeployedAmount;
    const interestPerSecond = vaultExpanded.vaultStrategy.strategy.interestPerSecond;
    const principalTokenBalance = vaultExpanded.vaultStrategy.strategy.tokenBalance;
    const outstandingInterestAmount = vaultExpanded.vaultStrategy.strategy.outstandingInterestAmount;
    const timeElapsed = Math.max(0, currentTimestamp - vaultExpanded.vaultStrategy.strategy.lastAccruedTimestamp);
    const interestEarned = interestPerSecond.mul(timeElapsed).add(outstandingInterestAmount);
    const externalYieldBalance = vaultExpanded.vaultStrategy.externalYieldInfo?.balance ?? 0;
    const tvl = new Decimal(principalDeployed).add(interestEarned).add(externalYieldBalance).add(principalTokenBalance);
    if (ignoreFees) return Number(tvl.toFixed(vaultExpanded.principalMetadata.decimals));
    const fees = getCurrentVaultFees(vaultExpanded, currentTimestamp);
    return Math.max(0, Number(tvl.sub(fees).toFixed(vaultExpanded.principalMetadata.decimals)));
}

export function getVaultTvl(vaultExpanded: LendingVaultExpanded | undefined, currentTimestamp: number) {
    return getVaultNetAssetValue(vaultExpanded, currentTimestamp, { ignoreFees: true });
}

export function getMaxVaultWithdraw(
    vaultExpanded: LendingVaultExpanded | undefined,
    userPosition: UserVaultPositionExpanded | undefined,
    now = getUnixTs()
): MaxVaultWithdraw | undefined {
    if (!vaultExpanded || !userPosition) return undefined;

    const summary = getVaultMaxAmountFromCap(vaultExpanded, CapType.Withdraw);
    const maxWithdrawCap = summary?.maxBalanceRemaining ?? DISABLED_APY_BIGINT;
    const vaultPrice = getVaultLpVirtualPrice(vaultExpanded, now) ?? 0;

    const globalMaxWithdrawPrincipal = getVaultBalanceAvailableToWithdraw(vaultExpanded, now);
    const userBalanceLp = userPosition.vaultLpBalance;
    const userBalancePrincipal = userBalanceLp * vaultPrice;

    const maxWithdrawCapPrincipal = safeBigIntToNumber(maxWithdrawCap);
    const maxWithdrawCapPrincipalUi =
        maxWithdrawCapPrincipal === Number.MAX_SAFE_INTEGER
            ? maxWithdrawCapPrincipal
            : lamportsToUiAmount(maxWithdrawCapPrincipal, vaultExpanded.principalMetadata.decimals);

    const maxWithdrawPrincipal = Math.min(globalMaxWithdrawPrincipal, userBalancePrincipal, maxWithdrawCapPrincipalUi);

    const globalMaxWithdrawLp = globalMaxWithdrawPrincipal / vaultPrice;
    const maxWithdrawCapLp = maxWithdrawCapPrincipal / vaultPrice;
    const maxWithdrawLp = maxWithdrawPrincipal / vaultPrice;

    return {
        maxWithdrawPrincipal,
        userBalancePrincipal,
        globalMaxWithdrawPrincipal,
        maxWithdrawLp,
        userBalanceLp,
        globalMaxWithdrawLp,
        maxWithdrawCapPrincipal,
        maxWithdrawCapLp
    };
}

export function useGetUserLpPositionAmounts({
    userPosition,
    vaultExpanded
}: {
    userPosition: UserVaultPositionExpanded | undefined;
    vaultExpanded: LendingVaultExpanded | undefined;
}): UserVaultPositionAmounts {
    const currentTimestamp = useLiveClock(PORTFOLIO_TICK);
    const { getUsdPrice } = useOraclePrices([vaultExpanded?.vault.principalMint]);

    return useMemo(() => {
        const totalAmount = userPosition?.vaultLpBalance ?? 0;
        const lpPrice = getVaultLpVirtualPrice(vaultExpanded, currentTimestamp);
        const principalPrice = getUsdPrice(vaultExpanded?.vault?.principalMint) ?? 0;
        const lpCollateralDeposits = userPosition?.lpCollateralDeposits ?? 0;

        const totalValuePrincipal = totalAmount * (lpPrice ?? 0);
        const totalValueUsd = totalValuePrincipal * principalPrice;

        const amountAvailablePrincipal = totalAmount * (lpPrice ?? 0);
        const amountAvailableUsd = amountAvailablePrincipal * principalPrice;

        const lpCollateralDepositsValueUsd = lpCollateralDeposits * (lpPrice ?? 0) * principalPrice;
        const lpCollateralDepositsValuePrincipal = lpCollateralDeposits * (lpPrice ?? 0);

        return {
            amountAvailable: {
                amountLp: totalAmount,
                amountUsd: amountAvailableUsd,
                amountPrincipal: amountAvailablePrincipal
            },
            totalAmount: {
                amountLp: totalAmount,
                amountUsd: totalValueUsd,
                amountPrincipal: totalValuePrincipal
            },
            lpCollateralDeposits: {
                amountLp: lpCollateralDeposits,
                amountUsd: lpCollateralDepositsValueUsd,
                amountPrincipal: lpCollateralDepositsValuePrincipal
            }
        };
    }, [userPosition, vaultExpanded, currentTimestamp, getUsdPrice]);
}

// Backed by /lending_vaults/user so the vault detail surface gets server-stamped
// USD / APY / PnL on the same pricing path as the portfolio section.
export function useUserVaultPositionByVault(vaultExpanded: LendingVaultExpanded | undefined) {
    const {
        data: { userWalletAddress: activeWallet }
    } = useLoopscaleWalletContext();

    const filter: UserLendingVaultFilter = {
        userAddress: activeWallet ?? "",
        vaultAddresses: vaultExpanded ? [vaultExpanded.vault.address] : undefined,
        sort: StakeSortType.WAvgApy,
        sortDirection: SortDirection.Desc,
        page: 0,
        pageSize: 1,
        status: UserVaultPositionStatus.Active
    };

    return useUserLendingVaultPositions(filter, !vaultExpanded || !activeWallet);
}

export function useUserVaultPositionExternalRewards(vaultExpanded?: LendingVaultExpanded | undefined) {
    const {
        data: { userWalletAddress: activeWallet }
    } = useLoopscaleWalletContext();

    const {
        data: externalRewardsPnlResponse,
        isLoading: externalRewardsPnlLoading,
        isFetching: externalRewardsPnlFetching
    } = useUserVaultExternalRewardsPnlQuery(
        {
            vaultAddresses: vaultExpanded ? [vaultExpanded.vault.address] : [],
            stakeAccounts: [],
            rewardMints: []
        },
        {
            skip: !activeWallet
        }
    );

    const { convertExternalRewardsResponse } = useAbfTypesToUiConverter();

    const data = useMemo(() => {
        if (!externalRewardsPnlResponse) return externalRewardsPnlResponse;
        return convertExternalRewardsResponse(externalRewardsPnlResponse);
    }, [externalRewardsPnlResponse, convertExternalRewardsResponse]);

    return {
        data,
        isLoading: externalRewardsPnlLoading,
        isFetching: externalRewardsPnlFetching
    };
}

export type UserLendingVaultPositionsResult = {
    data: UserVaultPositionExpanded[] | undefined;
    totalDataCount: number | undefined;
    aggregate: VaultDepositsAggregate | undefined;
    snapshotTs: number | undefined;
    isLoading: boolean;
    isFetching: boolean;
};

// Backed by /lending_vaults/user. Joins with /lending_vaults/info for vault metadata and
// reads server-stamped USD + APY fields off each position instead of client re-pricing.
export function useUserLendingVaultPositions(
    filter: UserLendingVaultFilter,
    skip?: boolean
): UserLendingVaultPositionsResult {
    const {
        data: { userWalletAddress: activeWallet }
    } = useLoopscaleWalletContext();

    const skipQuery = skip || !activeWallet;

    const {
        data: rawData,
        isLoading: positionsLoading,
        isFetching: positionsFetching
    } = useLendingVaultPositionsQuery(filter, { skip: skipQuery });

    const vaultAddresses = rawData?.items.map((p) => p.vaultAddress);

    const {
        data: vaults,
        isLoading: vaultsLoading,
        isFetching: vaultsFetching
    } = useLendVaults({
        skip: skipQuery,
        filter: { vaultAddresses }
    });

    // /lending_vaults/user stamps pnlUsd but does not echo the raw token-amount
    // `totalDeposits` / `totalWithdrawals`. Several client helpers
    // (`calculateVaultPnlFromDepositSummary`, `VaultProfitLoss`) still expect those token
    // amounts; joining the existing deposit-summary endpoint preserves that contract until
    // those helpers are swapped to server-stamped pnlUsd.
    const depositSummaryFilter = useMemo(() => ({ vaultAddresses: [], stakeAccounts: [] }), []);
    const { data: depositSummary } = useUserPositionDepositSummaryQuery(depositSummaryFilter, { skip: skipQuery });

    const addressToVault = useMemo(() => {
        if (!vaults) return undefined;
        return new Map(vaults.map((v) => [v.vault.address, v]));
    }, [vaults]);

    const isLoading = positionsLoading || vaultsLoading;
    const isFetching = positionsFetching || vaultsFetching;

    const data: UserVaultPositionExpanded[] | undefined = useMemo(() => {
        if (isLoading || isFetching || !vaults?.length) return undefined;
        if (!rawData?.items.length) return [];

        return rawData.items
            .map((item, index) => {
                const vaultExpanded = addressToVault?.get(item.vaultAddress);
                if (!vaultExpanded) return null;

                const lpDecimals = vaultExpanded.lpMetadata?.decimals ?? vaultExpanded.principalMetadata.decimals;
                const principalDecimals = vaultExpanded.principalMetadata.decimals;
                const summary = depositSummary?.[item.vaultAddress];

                const position: UserVaultPositionExpanded = {
                    index,
                    address: item.vaultAddress,
                    vaultExpanded,
                    vaultLpBalance: lamportsToUiAmount(item.availableLpShares, lpDecimals),
                    lpCollateralDeposits: lamportsToUiAmount(item.collateralLpShares, lpDecimals),
                    active: filter.status === UserVaultPositionStatus.Active,
                    // Populate from the deposit-summary join so legacy PnL helpers keep working.
                    // Post-B1, consumers move to server-stamped pnlUsd and these become unused.
                    totalDeposits: summary ? lamportsToUiAmount(summary.sumOfDeposits, principalDecimals) : 0,
                    totalWithdrawals: summary ? lamportsToUiAmount(summary.sumOfWithdrawals, principalDecimals) : 0,
                    startTime: item.startTime,
                    lastInteractedTimestamp: item.lastInteractedTime,
                    // Server-stamped USD + APY fields
                    positionValueUsd: item.positionValueUsd,
                    availableValueUsd: item.availableValueUsd,
                    collateralValueUsd: item.collateralValueUsd,
                    pnlUsd: item.pnlUsd,
                    baseApy: item.baseApy,
                    rewardApy: item.rewardApy,
                    collateralYieldApy: item.collateralYieldApy,
                    totalApy: item.totalApy
                };
                return position;
            })
            .filter(filterTruthy);
    }, [isLoading, isFetching, vaults?.length, rawData?.items, addressToVault, depositSummary, filter.status]);

    return {
        data,
        totalDataCount: rawData?.pageInfo.totalItems,
        aggregate: rawData?.aggregate,
        snapshotTs: rawData?.snapshotTs,
        isLoading,
        isFetching
    };
}

// Flat, server-paginated list of the wallet's lending-vault positions scoped to one principal-mint
// group (or all groups for ALL_VALUE). Analog of useUserStandardLoansPaginated.
export function useUserLendingVaultPositionsPaginated({
    pagination,
    principalGroupKey,
    skip
}: {
    pagination: UserLendingVaultFilter;
    principalGroupKey: AllValueType | string | number;
    skip?: boolean;
}): {
    positions: UserVaultPositionExpanded[] | undefined;
    totalCount: number | undefined;
    isLoading: boolean;
    isFetching: boolean;
} {
    const {
        data: { userWalletAddress: activeWallet }
    } = useLoopscaleWalletContext();

    const principalMint = isAll(principalGroupKey) ? undefined : String(principalGroupKey);
    // Destructure stable primitives off the (per-render) backendPagination object so the filter
    // recomputes only when a meaningful input changes.
    const { page, pageSize, sort, sortDirection } = pagination;

    const filter = useMemo(
        (): UserLendingVaultFilter => ({
            sort,
            sortDirection,
            page,
            pageSize,
            userAddress: activeWallet ?? "",
            principalMints: principalMint ? [principalMint] : [],
            vaultAddresses: [],
            lpMints: [],
            status: UserVaultPositionStatus.Active
        }),
        [page, pageSize, sort, sortDirection, activeWallet, principalMint]
    );

    const { data, totalDataCount, isLoading, isFetching } = useUserLendingVaultPositions(filter, skip || !activeWallet);

    return { positions: data, totalCount: totalDataCount, isLoading, isFetching };
}

export function useUserVaultDepositSummaryByVault(vaultAddress: string | undefined) {
    const { data, isLoading, isFetching } = useUserVaultPnlSummary({
        filter: { vaultAddresses: vaultAddress ? [vaultAddress] : [] }
    });

    return {
        data: vaultAddress ? data?.[vaultAddress] : undefined,
        isLoading,
        isFetching
    };
}

export function useUserVaultDepositSummarys() {
    const { data, isLoading, isFetching } = useUserVaultPnlSummary({ filter: {} });

    return {
        data: isLoading || isFetching ? undefined : data,
        isLoading,
        isFetching
    };
}

function useUserVaultPnlSummary({ filter }: { filter: Partial<UserPositionDepositSummaryParams> }) {
    const {
        data: { userWalletAddress }
    } = useLoopscaleWalletContext();

    const rawFilter = useMemo(
        () => ({
            stakeAccounts: filter?.stakeAccounts ?? [],
            vaultAddresses: filter?.vaultAddresses ?? []
        }),
        [filter]
    );

    const {
        data: depositSummaries,
        isLoading,
        isFetching
    } = useUserPositionDepositSummaryQuery(rawFilter, {
        skip: (filter.vaultAddresses && filter.vaultAddresses.length === 0) || !userWalletAddress // skip when a filter is provided but there is no address populated, else we want everything
    });

    const vaultAddresses = useMemo(() => {
        if (!depositSummaries) return [];
        const addresses = new Set<string>();
        Object.keys(depositSummaries).forEach((address) => {
            addresses.add(address);
        });
        return Array.from(addresses);
    }, [depositSummaries]);

    const { data: vaults } = useLendVaults({
        filter: { vaultAddresses },
        skip: !vaultAddresses.length
    });

    const vaultMap = useMemo(() => {
        if (!vaults) return undefined;
        return new Map(vaults.map((v) => [v.vault.address, v]));
    }, [vaults]);

    const { convertLendingVaultDepositSummary } = useAbfTypesToUiConverter();

    const data = useMemo(() => {
        if (!depositSummaries || !vaultMap?.size) return undefined;
        return convertLendingVaultDepositSummary(depositSummaries, vaultMap);
    }, [depositSummaries, vaultMap, convertLendingVaultDepositSummary]);

    return { data, isLoading, isFetching };
}

export function useLendingVaultAllocation(vaultAddress: string | undefined, principalMint: string | undefined) {
    const query = useLendingVaultAllocationsQuery(vaultAddress ? { vaultAddress } : skipToken, {
        skip: !vaultAddress || !principalMint
    });

    const { getMetadata, isLoading: metadataLoading } = useTokenListContext();
    const { convertLendingVaultAllocation } = useAbfTypesToUiConverter();

    const isLoading = query.isLoading || metadataLoading;
    const data = useMemo(() => {
        if (isLoading) return undefined;
        return query.data
            ?.map((rawAllocation): NullableRecord<LendingVaultAllocationExpanded> => {
                const collateralMetadata = getMetadata(rawAllocation.collateralMint);
                const principalMetadata = getMetadata(principalMint);
                const allocation = convertLendingVaultAllocation(rawAllocation, principalMetadata?.decimals ?? 0);
                return {
                    ...allocation,
                    collateralMetadata
                };
            })
            .filter(filterNullableRecord);
    }, [isLoading, query.data, getMetadata, principalMint, convertLendingVaultAllocation]);
    return { data, isLoading };
}

export function isVaultManager(vaultExpanded: LendingVaultExpanded | undefined, activeWallet: string | undefined) {
    if (!vaultExpanded || !activeWallet) return false;
    return vaultExpanded.vault.manager === activeWallet;
}

export function getVaultIdleBalance(
    vaultExpanded: LendingVaultExpanded,
    currentTimestamp: number,
    options: { ignoreFees: boolean } = { ignoreFees: false }
) {
    if (!vaultExpanded.vaultStrategy) return 0;
    let totalIdleBalance = 0;
    if (vaultExpanded.vaultStrategy.externalYieldInfo) {
        totalIdleBalance += vaultExpanded.vaultStrategy.externalYieldInfo.balance;
    }
    totalIdleBalance += vaultExpanded.vaultStrategy.strategy.tokenBalance;
    if (options.ignoreFees) return totalIdleBalance;
    return Math.max(0, totalIdleBalance - getCurrentVaultFees(vaultExpanded, currentTimestamp));
}

export function getVaultBalanceAvailableToWithdraw(vaultExpanded: LendingVaultExpanded, currentTimestamp: number) {
    return getVaultIdleBalance(vaultExpanded, currentTimestamp, { ignoreFees: true });
}

export function getVaultInterestAccrued(vaultExpanded: LendingVaultExpanded, currentTimestamp: number): number {
    if (!vaultExpanded.vaultStrategy) return 0;
    return Math.max(
        0,
        Number(
            vaultExpanded.vaultStrategy.strategy.interestPerSecond
                .mul(currentTimestamp - vaultExpanded.vaultStrategy.strategy.lastAccruedTimestamp)
                .toFixed(vaultExpanded.principalMetadata.decimals)
        )
    );
}

export function getCurrentVaultFees(vaultExpanded: LendingVaultExpanded, currentTimestamp: number) {
    if (!vaultExpanded.vaultStrategy) return 0;
    const interestAccruedValue = getVaultInterestAccrued(vaultExpanded, currentTimestamp);
    return (
        interestAccruedValue * vaultExpanded.vaultStrategy.strategy.interestFee +
        vaultExpanded.vaultStrategy.strategy.feeClaimable
    );
}

export function getVaultLoopscalePointsFromStakeRewards(stakeRewards: VaultReward | undefined) {
    if (!stakeRewards) return LoopscalePointsAction.LiquidStake;
    if (stakeRewards.baseReward.reward) {
        return stakeRewards.baseReward.reward as LoopscalePointsAction;
    }
    return LoopscalePointsAction.LiquidStake;
}

export function isExternalYieldUpdated(
    vaultExpanded: LendingVaultExpanded | undefined,
    userVaultPnl: UserVaultPnl | undefined
) {
    if (!vaultExpanded || !userVaultPnl?.lastInteractedTimestamp) return false;
    if (!vaultExpanded.vaultStrategy?.externalYieldInfo) return true;

    return vaultExpanded.vaultStrategy.externalYieldInfo.lastUpdateTime >= userVaultPnl.lastInteractedTimestamp;
}

export function useUserVaultPnlFromDepositSummary(
    vaultExpanded: LendingVaultExpanded | undefined,
    userPosition: UserVaultPositionExpanded | undefined
): UserVaultPnl | undefined {
    const now = useLiveClock(PORTFOLIO_TICK);
    const { totalAmount } = useGetUserLpPositionAmounts({
        userPosition,
        vaultExpanded
    });

    // reflects the total lp amount. should not be displayed in position value as the lpCollateralDeposits is shown in tooltip
    // you could altneratively add this to the total position value or subtract it from the withdraws, but I think this is the most straightforward
    // current solution until we know better how we want to display this
    const totalLpStaked = totalAmount.amountLp + (userPosition?.lpCollateralDeposits ?? 0);
    const vaultPnl = calculateVaultPnlFromDepositSummary(userPosition, vaultExpanded, now, totalLpStaked);

    const userVaultPnl: UserVaultPnl = {
        vaultPnl,
        lastInteractedTimestamp: userPosition?.lastInteractedTimestamp
    };

    return userVaultPnl;
}

function calculateVaultPnlFromDepositSummary(
    userPosition: UserVaultPositionExpanded | undefined,
    vaultExpanded: LendingVaultExpanded | undefined,
    currentTimestamp: number,
    totalLpAmount: number
) {
    if (!vaultExpanded || !userPosition) return undefined;
    const virtualPrice = getVaultLpVirtualPrice(vaultExpanded, currentTimestamp);
    if (!virtualPrice) return undefined;
    const positionValue = virtualPrice * totalLpAmount;
    const netFlows = userPosition.totalDeposits - userPosition.totalWithdrawals;

    return positionValue - netFlows;
}

export function isSyntheticRewardsSchedule(schedule: ParsedVaultRewardsScheduleExpanded) {
    return schedule.synthetic;
}

export function isRewardsActive(schedule: ParsedVaultRewardsScheduleExpanded) {
    return schedule.rewardEndTime > getUnixTs() && schedule.rewardStartTime < getUnixTs();
}

export function summarizeRewardsSchedule(
    schedule: ParsedVaultRewardsScheduleExpanded,
    vaultEmittedRewards: Record<number, number> | undefined
) {
    const timeSinceRewardsStart = getUnixTs() - schedule.rewardStartTime;

    const totalEmittedSinceStartTime = lamportsToUiAmount(
        vaultEmittedRewards?.[schedule.scheduleIndex] ?? 0,
        schedule.rewardMetadata.decimals
    );

    return {
        isActive: isRewardsActive(schedule),
        totalEmittedSinceStartTime,
        timeSinceRewardsStart
    };
}

export function getRewardsScheduleStats(
    schedule: ParsedVaultRewardsScheduleExpanded,
    vaultEmittedRewards: Record<number, number> | undefined
) {
    const { isActive, totalEmittedSinceStartTime } = summarizeRewardsSchedule(schedule, vaultEmittedRewards);

    const totalEmissionAmount = schedule.totalEmissionsAmount;
    const totalEmitted = Math.min(totalEmissionAmount, totalEmittedSinceStartTime);
    const unemittedRewards = totalEmissionAmount - totalEmitted;

    const totalDuration = schedule.rewardEndTime - schedule.rewardStartTime;
    const emissionsPerWeek = totalDuration > 0 ? (totalEmissionAmount / totalDuration) * TIME.WEEK : 0;

    return {
        isActive,
        totalEmitted,
        totalEmittedSinceStartTime,
        unemittedRewards,
        emissionsPerWeek,
        totalEmissionAmount
    };
}

export function getVaultStakeWeightDurations() {
    return [
        { duration: ParsedAllowedStakeDuration.ZeroDays, label: "Liquid stake" },
        { duration: ParsedAllowedStakeDuration.OneMonth, label: "1 Month" },
        { duration: ParsedAllowedStakeDuration.ThreeMonths, label: "3 Months" },
        { duration: ParsedAllowedStakeDuration.SixMonths, label: "6 Months" },
        { duration: ParsedAllowedStakeDuration.OneYear, label: "1 Year" }
    ];
}

export function recalculateStrategyApyWithDeposit({
    lendingStrategyExpanded,
    depositAmount
}: {
    lendingStrategyExpanded: StrategyExpandedInfo;
    depositAmount: number;
}) {
    const idleApy = new Decimal(lendingStrategyExpanded?.externalYieldInfo?.apy ?? 0);

    const idleYieldBalance = new Decimal(lendingStrategyExpanded?.externalYieldInfo?.balance ?? 0);

    const idleYieldBalanceWithDeposit = idleYieldBalance.add(new Decimal(depositAmount));

    const currentDeployedAmount = new Decimal(lendingStrategyExpanded?.strategy.currentDeployedAmount ?? 0);

    const tokenBalance = new Decimal(lendingStrategyExpanded?.strategy.tokenBalance ?? 0);

    const totalDeposits = currentDeployedAmount.add(new Decimal(depositAmount)).add(idleYieldBalance).add(tokenBalance);

    if (totalDeposits.eq(0)) return 0;

    const newApy = idleApy
        .mul(idleYieldBalanceWithDeposit)
        .add(currentDeployedAmount.mul(lendingStrategyExpanded?.strategy.fixedApy))
        .div(totalDeposits);

    return newApy.toNumber();
}

export function getLendingVaultMints(lendingVaultExpanded: LendingVaultExpanded | undefined) {
    const rewardMints = lendingVaultExpanded?.vaultRewardsSchedules.map((schedule) => schedule.rewardMetadata.mint);
    const principalMint = lendingVaultExpanded?.vault.principalMint;
    const rewardMintsWithPrincipal = combineCollections([rewardMints, [principalMint]]);
    return rewardMintsWithPrincipal;
}

export function getVaultNetApy(
    lendingVaultExpanded: LendingVaultExpanded | undefined,
    getUsdPrice: (mint: string) => number | undefined,
    collateralYields: Record<string, number> | undefined
): number {
    if (!lendingVaultExpanded || !lendingVaultExpanded.vaultStrategy || !collateralYields) return 0;
    if (isVaultTemporarilyUnavailable(lendingVaultExpanded)) return 0;

    const baseApy = lendingVaultExpanded.vaultStrategy.strategy.wAvgApy;

    const now = getUnixTs();
    const lpPrice = getVaultLpVirtualPrice(lendingVaultExpanded, now);
    const principalPrice = getUsdPrice(lendingVaultExpanded?.vault.principalMint);
    const lpSupply = lendingVaultExpanded.vault.lpSupply;

    const collateralYield = collateralYields?.[lendingVaultExpanded?.vault.principalMint] ?? 0;

    const rewardApys = lendingVaultExpanded.vaultRewardsSchedules
        .filter(isRewardsActive)
        .map((schedule) => {
            const rewardPrice = getUsdPrice(schedule.rewardMetadata.mint);
            return rewardPrice !== undefined
                ? convertExternalRewardToApy(
                      schedule,
                      lpPrice,
                      principalPrice,
                      lpSupply,
                      rewardPrice,
                      0 // depositAmount is 0 for net APY
                  )
                : undefined;
        })
        .filter(filterTruthy)
        .reduce((total, apy) => total + apy, 0);

    return baseApy + rewardApys + collateralYield;
}

export function useVaultApyBreakdown({
    lendingVaultExpanded,
    includeRewards = false,
    depositAmount = 0
}: {
    lendingVaultExpanded: LendingVaultExpanded | undefined;
    includeRewards?: boolean;
    depositAmount?: number;
}): VaultApyBreakdown | undefined {
    const rewardTokenMintsWithPrincipal = getLendingVaultMints(lendingVaultExpanded);
    const { getUsdPrice } = useOraclePrices(rewardTokenMintsWithPrincipal);
    const { data: collateralYields } = useLatestCollateralYieldHistoryByMint(
        lendingVaultExpanded?.vault.principalMint ? [lendingVaultExpanded?.vault.principalMint] : []
    );

    if (
        !lendingVaultExpanded ||
        !lendingVaultExpanded.vaultStrategy ||
        !rewardTokenMintsWithPrincipal ||
        !collateralYields
    )
        return undefined;

    const baseApy =
        depositAmount > 0
            ? recalculateStrategyApyWithDeposit({
                  lendingStrategyExpanded: lendingVaultExpanded.vaultStrategy,
                  depositAmount
              })
            : lendingVaultExpanded.vaultStrategy.strategy.wAvgApy;

    const now = getUnixTs();
    const lpPrice = getVaultLpVirtualPrice(lendingVaultExpanded, now);
    const principalPrice = getUsdPrice(lendingVaultExpanded?.vault.principalMint);
    const lpSupply = lendingVaultExpanded.vault.lpSupply;

    const collateralYield = collateralYields?.[lendingVaultExpanded?.vault.principalMint] ?? 0;

    let rewardApys = 0;

    if (includeRewards) {
        rewardApys = lendingVaultExpanded.vaultRewardsSchedules.reduce((total, schedule) => {
            if (!isRewardsActive(schedule)) return total;
            const rewardPrice = getUsdPrice(schedule.rewardMetadata.mint);
            if (rewardPrice === undefined) return total;
            const apy = convertExternalRewardToApy(
                schedule,
                lpPrice,
                principalPrice,
                lpSupply,
                rewardPrice,
                depositAmount
            );
            return apy ? total + apy : total;
        }, 0);
    }

    return {
        baseApy,
        rewardApy: rewardApys,
        collateralYield,
        totalApy: baseApy + rewardApys + collateralYield
    };
}

export function convertExternalRewardToApy(
    schedule: ParsedVaultRewardsScheduleExpanded,
    lpPrice: number | undefined,
    principalPrice: number | undefined,
    lpSupply: number,
    rewardTokenPrice: number,
    depositAmount = 0
) {
    const { emissionsPerSecond } = schedule;

    const emissionsPerYear = new Decimal(emissionsPerSecond).mul(new Decimal(TIME.YEAR));
    const lpSupplyDecimal = new Decimal(lpSupply);
    if (!lpPrice || !principalPrice) return undefined;

    // new lp amount if deposited = depositAmount * lpPrice + lp supply

    const newLpSupply =
        depositAmount > 0 ? lpSupplyDecimal.add(new Decimal(depositAmount).mul(lpPrice)) : lpSupplyDecimal;

    const denominator = newLpSupply.mul(lpPrice).mul(principalPrice);
    const numerator = emissionsPerYear.mul(rewardTokenPrice);
    const annualYield = numerator.div(denominator).toNumber();

    return annualYield;
}

export function isVaultSupplyAvailable(vaultExpanded: LendingVaultExpanded | undefined) {
    if (isVaultTemporarilyUnavailable(vaultExpanded)) return false;
    const { supplyLimitReached } = summarizeVaultSuppliedDeposits(vaultExpanded);
    if (supplyLimitReached) return false;
    return true;
}

export function summarizeVaultSuppliedDeposits(vaultExpanded: LendingVaultExpanded | undefined) {
    const principalLimit = vaultExpanded?.vaultMetadata?.depositCap ?? 0;
    const principalSupplied = getVaultTvl(vaultExpanded, getUnixTs()) ?? 0;
    const supplyLimitReached = principalLimit > 0 ? principalSupplied >= principalLimit : false;

    return {
        isDepositRestricted: !!vaultExpanded?.vaultMetadata?.depositCap,
        principalSupplied,
        supplyLimitReached,
        principalLimit
    };
}

/**
 * Flattens `UserVaultExternalRewardsPnlResponse` (vault → stake → reward mint) into
 * one map per vault address with rewards aggregated by reward mint (sums pending/claimed
 * across stake accounts). Used for portfolio tables and tooltips.
 */
export function buildExternalRewardsByVault(
    rewards: UserVaultExternalRewardsPnlResponse | null | undefined
): Record<string, Record<RewardMint, UserExternalRewardsInfoExpanded>> | undefined {
    if (rewards === undefined) return undefined;
    if (rewards === null) return {};

    const out: Record<string, Record<RewardMint, UserExternalRewardsInfoExpanded>> = {};

    for (const vaultAddress in rewards) {
        const rewardsByStake = rewards[vaultAddress];
        if (!rewardsByStake) continue;

        const aggregatedByMint: Record<string, UserExternalRewardsInfoExpanded> = {};

        for (const stakeAccount in rewardsByStake) {
            const stakeRewards = rewardsByStake[stakeAccount];
            if (!stakeRewards) continue;

            for (const mint in stakeRewards) {
                const reward = stakeRewards[mint];
                if (!reward) continue;

                const existing = aggregatedByMint[mint];
                if (existing) {
                    existing.pendingRewards = (existing.pendingRewards ?? 0) + (reward.pendingRewards ?? 0);
                    existing.claimedRewards = (existing.claimedRewards ?? 0) + (reward.claimedRewards ?? 0);
                } else {
                    aggregatedByMint[mint] = {
                        ...reward,
                        pendingRewards: reward.pendingRewards ?? 0,
                        claimedRewards: reward.claimedRewards ?? 0
                    };
                }
            }
        }

        out[vaultAddress] = aggregatedByMint;
    }

    return out;
}

export function getMintsFromExternalRewards(
    externalRewardsPnlResponse: AggregatedUserExternalRewardsPnlResponse | null | undefined
): string[] {
    if (!externalRewardsPnlResponse) return [];

    const mints = new Set<string>();

    Object.values(externalRewardsPnlResponse).forEach((vaultRewards) => {
        Object.keys(vaultRewards).forEach((mint) => {
            mints.add(mint);
        });
    });

    return Array.from(mints);
}

export function convertExternalRewardsToAggregatedRewards(
    vaultExpanded: LendingVaultExpanded | undefined,
    externalRewardsPnlResponse: UserVaultExternalRewardsPnlResponse | null | undefined
): AggregatedUserExternalRewardsPnlResponse | null | undefined {
    if (!vaultExpanded || externalRewardsPnlResponse === undefined) return undefined;

    const vaultAddress = vaultExpanded.vault.address;
    const vaultRewards = externalRewardsPnlResponse?.[vaultAddress];

    if (!vaultRewards) {
        return null;
    }

    // Create a map to aggregate rewards by mint
    const mintMap = new Map<string, UserExternalRewardsInfoExpanded>();

    // Aggregate rewards across all stake accounts for this vault
    Object.values(vaultRewards).forEach((stakeRewards) => {
        Object.entries(stakeRewards).forEach(([mint, reward]) => {
            const existingReward = mintMap.get(mint);
            if (existingReward) {
                // Sum up rewards if mint already exists
                existingReward.pendingRewards += reward.pendingRewards;
                existingReward.claimedRewards += reward.claimedRewards;
            } else {
                // Add new reward entry if mint doesn't exist
                mintMap.set(mint, { ...reward });
            }
        });
    });

    // Return null if no rewards were found
    if (mintMap.size === 0) {
        return null;
    }

    return {
        [vaultAddress]: Object.fromEntries(mintMap)
    };
}

export type ParsedLendVaultActionWithContribution = ParsedLendVaultAction & { contribution: number };

export function calculateContributionsFromActions(
    vaultActions: ParsedLendVaultAction[],
    latestFirst = false
): ParsedLendVaultActionWithContribution[] {
    let runningContribution = 0;

    // Sort actions by timestamp to ensure chronological order for correct contribution calculation.
    const sortedActions = [...vaultActions].sort((a, b) => a.timestamp - b.timestamp);

    // Calculate contributions in chronological order
    const mapped = sortedActions.map((vaultAction) => {
        const isWithdrawal = vaultAction.actionType === VaultActionType.Withdrawal;
        runningContribution += isWithdrawal ? -vaultAction.amount : vaultAction.amount;

        return { ...vaultAction, contribution: runningContribution };
    });

    return latestFirst ? mapped.reverse() : mapped;
}

// Helper function to check if a value is disabled, we treat U64.MAX as disabled
export function isDisabledValue(value: bigint | undefined): boolean {
    return value === undefined || value === DISABLED_APY_BIGINT;
}

export function isVaultLpCollateral(metadata: TokenListMetadata | undefined) {
    if (!metadata) return false;
    return metadata?.tags?.includes(TokenListTag.Vault) ?? false;
}

export type UserVaultsResult = {
    groups: UserVaultGroup[] | undefined;
    totalGroups: number | undefined;
    aggregate: VaultDepositsAggregate | undefined;
    snapshotTs: number | undefined;
    isLoading: boolean;
    isFetching: boolean;
};

/** Grouped lend vault portfolio (`POST /user/vaults`), same layering as {@link useUserStrategies}. */
/**
 * Temporary incident patch: `/user/vaults` only returns server-combined group totals, so a USDC Genesis
 * holder's totals come from `/lending_vaults/user`, whose responses are corrected on parse. `allPositions`
 * uses the portfolio overview's exact filter, so the two share a cache entry.
 */
function useGenesisCorrectedVaultPositions(wallet: string | undefined, status: number | undefined, skip: boolean) {
    const baseFilter = {
        userAddress: wallet ?? "",
        vaultAddresses: [],
        lpMints: [],
        sort: StakeSortType.WAvgApy,
        sortDirection: SortDirection.Desc,
        status: status ?? UserVaultPositionStatus.Active,
        page: 0
    };
    const usdcQuery = useLendingVaultPositionsQuery(
        { ...baseFilter, principalMints: [USDC_GENESIS_PRINCIPAL_MINT], pageSize: MAXIMUM_PAGE_SIZE },
        { skip }
    );
    const allQuery = useLendingVaultPositionsQuery({ ...baseFilter, pageSize: 20 }, { skip });

    return {
        usdcPositions: usdcQuery.data,
        allPositions: allQuery.data,
        isLoading: usdcQuery.isLoading || allQuery.isLoading
    };
}

/** Swaps a USDC group's totals for corrected ones. Native amounts keep the server's native-per-USD ratio. */
export function correctGenesisVaultGroup(
    group: ApiUserVaultGroup,
    usdcPositions: VaultDepositResponse
): ApiUserVaultGroup {
    const nativePerUsd = Number(group.totalValueNative) / group.aggregate.totalValueUsd || 0;
    const toNative = (usd: number) => String(Math.round(usd * nativePerUsd));
    const sumUsd = (getUsd: (item: VaultDepositResponse["items"][number]) => number) =>
        usdcPositions.items.reduce((sum, item) => sum + getUsd(item), 0);

    return {
        ...group,
        aggregate: { ...group.aggregate, ...usdcPositions.aggregate },
        totalValueNative: toNative(usdcPositions.aggregate.totalValueUsd),
        availableValueNative: toNative(sumUsd((item) => item.availableValueUsd)),
        collateralValueNative: toNative(sumUsd((item) => item.collateralValueUsd))
    };
}

export function useUserVaults(filter: Partial<UserVaultsFilter>, skip?: boolean): UserVaultsResult {
    const {
        data: { userWalletAddress: activeWallet }
    } = useLoopscaleWalletContext();

    const rawFilter = useMemo((): UserVaultsFilter => {
        return {
            userAddress: activeWallet,
            principalMints: filter.principalMints ?? [],
            vaultAddresses: filter.vaultAddresses ?? [],
            lpMints: filter.lpMints ?? [],
            status: filter.status ?? UserVaultPositionStatus.Active,
            sortDirection: filter.sortDirection ?? SortDirection.Desc,
            page: filter.page ?? 0,
            pageSize: filter.pageSize ?? 25
        };
    }, [
        activeWallet,
        filter.principalMints,
        filter.vaultAddresses,
        filter.lpMints,
        filter.status,
        filter.sortDirection,
        filter.page,
        filter.pageSize
    ]);

    const shouldSkip = !activeWallet || skip;
    const {
        usdcPositions,
        allPositions,
        isLoading: genesisCorrectionLoading
    } = useGenesisCorrectedVaultPositions(activeWallet, rawFilter.status, !!shouldSkip);
    const { data, isLoading: queryLoading, isFetching } = useUserVaultsGroupsQuery(rawFilter, { skip: shouldSkip });

    const { tokensLoading, getMetadata } = useAbfTypesToUiConverter();
    // Held back so a holder never briefly sees the inflated group values
    const isLoading = queryLoading || tokensLoading || genesisCorrectionLoading;
    const holdsGenesis = !!usdcPositions?.items.some((item) => item.vaultAddress === USDC_GENESIS_VAULT_ADDRESS);

    const groups = useMemo((): UserVaultGroup[] | undefined => {
        if (isLoading) return undefined;
        if (shouldSkip || !data) return [];
        return data.groups
            .map((rawGroup: ApiUserVaultGroup): NullableRecord<UserVaultGroup> => {
                const g =
                    holdsGenesis && usdcPositions && rawGroup.principalMint === USDC_GENESIS_PRINCIPAL_MINT
                        ? correctGenesisVaultGroup(rawGroup, usdcPositions)
                        : rawGroup;
                return {
                    principalMint: g.principalMint,
                    principalMetadata: getMetadata(g.principalMint),
                    aggregate: g.aggregate,
                    totalValueNative: Number(g.totalValueNative) || 0,
                    availableValueNative: Number(g.availableValueNative) || 0,
                    collateralValueNative: Number(g.collateralValueNative) || 0
                };
            })
            .filter(filterNullableRecord);
    }, [isLoading, shouldSkip, data, getMetadata, holdsGenesis, usdcPositions]);

    const isUnfiltered = !filter.principalMints?.length && !filter.vaultAddresses?.length && !filter.lpMints?.length;

    return {
        groups: isLoading ? undefined : groups,
        totalGroups: data?.pageInfo.totalItems,
        aggregate: holdsGenesis && isUnfiltered && allPositions ? allPositions.aggregate : data?.aggregate,
        snapshotTs: data?.snapshotTs,
        isLoading,
        isFetching
    };
}

/** Table / checkbox group key for {@link UserVaultGroup} rows (the principal mint). */
export function userVaultGroupTableKey(group: Pick<UserVaultGroup, "principalMint">): string {
    return group.principalMint;
}

export function useLendingVaultPositionsGroupedByMint(
    principalMints: string[],
    options?: { skip?: boolean }
): {
    allPositions: UserVaultPositionExpanded[] | undefined;
    positionsByMint: Map<string, UserVaultPositionExpanded[]> | undefined;
    isLoading: boolean;
} {
    const {
        data: { userWalletAddress: activeWallet }
    } = useLoopscaleWalletContext();

    const filter = useMemo((): UserLendingVaultFilter => {
        return {
            userAddress: activeWallet ?? "",
            principalMints,
            vaultAddresses: [],
            lpMints: [],
            sort: StakeSortType.AmountStaked,
            sortDirection: SortDirection.Asc,
            page: 0,
            pageSize: MAXIMUM_PAGE_SIZE,
            status: UserVaultPositionStatus.Active
        };
    }, [activeWallet, principalMints]);

    const skip = options?.skip || !activeWallet || principalMints.length === 0;
    const { data: allPositions, isLoading } = useUserLendingVaultPositions(filter, skip);

    const positionsByMint = useMemoizedGroupedMap<UserVaultPositionExpanded, string>(
        allPositions,
        (p) => p.vaultExpanded.vault.principalMint
    );

    return { allPositions, positionsByMint, isLoading };
}

export function getVaultLpStakeUnstakeIxParams({
    vault,
    amountLp,
    skip
}: {
    vault: string | undefined;
    amountLp: number | undefined;
    skip: boolean;
}): StakeLpQueryParams | UnstakeLpQueryParams | undefined {
    if (!vault || skip) return undefined;
    if (amountLp === undefined) return undefined;
    return { vault, amount: amountLp };
}
